import React, { useEffect } from "react";
import "./style.scss";
import { Link } from "react-router-dom";
import {
  scrollToTop,
  duwise_hero1,
  duwise_hero2,
  duwise_logo,
} from "src/shared/utils/appConstant";
import { pageRoutes } from "src/shared/utils/pageRoutes.constant";
import HomePageFooter from "src/views/home/components/Footer";
import Footer from "src/components/footer";

const DuWise: React.FC = () => {
  useEffect(() => {
    scrollToTop();
  }, []);

  const timelineSteps = [
    { num: 1, title: "Capture" },
    { num: 2, title: "Organize" },
    { num: 3, title: "Track" },
    { num: 4, title: "Remind" },
    { num: 5, title: "Sync" },
  ];

  const impactCards = [
    {
      num: "1.",
      title: "Higher Engagement & Retention:",
      description:
        "Expiry and reminder workflows create recurring touchpoints. Users have a reason to return when important dates approach.",
    },
    {
      num: "2.",
      title: "Data Continuity:",
      description:
        "Offline queues and cloud synchronization reduce dependence on a continuously available network and support device changes.",
    },
    {
      num: "3.",
      title: "Reliability:",
      description:
        "Firebase Crashlytics provides crash/error visibility to help the team diagnose issues and improve stability.",
    },
  ];

  const problemCards = [
    {
      title: "Scattered Documents",
      problem: "Documents are scattered across phones, folders, and email.",
      solution: "Central dashboard with structured metadata and folder-based categories.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      ),
    },
    {
      title: "Forgotten Expiry Dates",
      problem: "Users forget expiry dates and incur late fees.",
      solution: "Expiry tracking with configurable alert/push notification reminders.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <circle cx="12" cy="15" r="2" />
        </svg>
      ),
    },
    {
      title: "Friction in Connectivity",
      problem: "Internet connectivity is erratic and weak.",
      solution: "Local document accessibility won't fail with connectivity issues.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
          <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
          <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
          <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
          <line x1="12" y1="20" x2="12.01" y2="20" />
        </svg>
      ),
    },
    {
      title: "Phone Replacement Risk",
      problem: "Phone replacement can cause loss of local files.",
      solution: "Cloud backup and restoration for re-installed users.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
          <line x1="12" y1="18" x2="12.01" y2="18" />
        </svg>
      ),
    },
    {
      title: "Language Barriers",
      problem: "Users speak different languages / regions.",
      solution: "Internationalized language preferences (multi-method UI/config) across all screens.",
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="duwise-container animate__animated animate__fadeIn">
      {/* 1. Hero Section */}
      <section className="duwise-hero">
        <div className="hero-backdrop-pillar" />
        <div className="hero-content-left animate__animated animate__fadeInLeft">
          <h1 className="hero-title">DuWise</h1>

          <p className="hero-subtitle">
            Effortlessly centralize documents, set timely reminders, and enjoy seamless accessibility for unmatched organizational efficiency.
          </p>
        </div>

        {/* Right Hero Logo */}
        <div className="hero-shield-right animate__animated animate__fadeInRight">
          <img src={duwise_logo} alt="DuWise Logo" className="duwise-logo-img" />
        </div>

        {/* Breadcrumb Navigation */}
        <div className="hero-breadcrumb">
          <Link to={pageRoutes.Home} onClick={scrollToTop}>
            Home
          </Link>
          <span className="breadcrumb-sep">/</span>
          <Link to={pageRoutes.Marketplace} onClick={scrollToTop}>
            Marketplace
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-active">DuWise</span>
        </div>
      </section>

      {/* 2. "What is DuWise?" Section */}
      <section className="what-is-duwise-section">
        <div className="what-is-container">
          <div className="what-is-text animate__animated animate__fadeInUp">
            <div className="what-is-header-row">
              <h2 className="outline-title">
                What is
                <br />
                <span className="duwise-outline-row">
                  DuWise?
                  <span className="mobile-dots" aria-hidden="true">
                    <span className="dot dot-red" />
                    <span className="dot dot-blue" />
                    <span className="dot dot-red" />
                  </span>
                </span>
              </h2>
            </div>

            {/* In mobile, mockup is displayed between title and description */}
            <div className="what-is-mockup-mobile">
              <div className="duwise-layered-mockup">
                <img
                  src={duwise_hero1}
                  alt="DuWise Overview Screen"
                  className="duwise-mockup-bg"
                />
                <img
                  src={duwise_hero2}
                  alt="DuWise Document Detail Screen"
                  className="duwise-mockup-fg"
                />
              </div>
            </div>

            <p className="what-is-p">
              <strong className="highlight-brand">DuWise</strong> is a mobile-first application for managing important personal documents. Users can capture documents through camera/gallery, enter information, store the associated files, and manage the resulting records from a central dashboard.
            </p>

            <p className="what-is-p">
              The product extends beyond a digital filing cabinet by adding expiry awareness, reminder configuration, search/sort, edit/delete, offline usage, cloud backup, cross-device restoration, localization, and crash monitoring.
            </p>
          </div>

          <div className="what-is-mockup animate__animated animate__zoomIn">
            <div className="duwise-layered-mockup">
              <img
                src={duwise_hero1}
                alt="DuWise Overview Screen"
                className="duwise-mockup-bg"
              />
              <img
                src={duwise_hero2}
                alt="DuWise Document Detail Screen"
                className="duwise-mockup-fg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 3. "How DuWise transforms document & product tracking" Section */}
      <section className="transforms-section">
        <div className="transforms-header">
          <h2 className="transforms-title">
            How DuWise transforms{" "}
            <span className="highlight-blue">document</span> &amp;{" "}
            <span className="highlight-blue">product tracking</span>
          </h2>
          <p className="transforms-desc">
            DuWise combines document capture, structured metadata, expiry reminder tracking, offline saving, and cloud backup/synchronization in one experience.
          </p>
        </div>

        {/* Desktop Horizontal Timeline */}
        <div className="process-timeline-desktop">
          <div className="timeline-numbers-row">
            {timelineSteps.map((step) => (
              <div className="timeline-num-badge" key={step.num}>
                {step.num}
              </div>
            ))}
          </div>

          <div className="timeline-circles-row">
            {timelineSteps.map((step) => (
              <div className="process-circle-card" key={step.num}>
                <span className="circle-text">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Vertical Timeline */}
        <div className="process-timeline-mobile">
          {timelineSteps.map((step) => (
            <div className="mobile-step-unit" key={step.num}>
              <div className="mobile-num-badge">{step.num}</div>
              <div className="mobile-circle-card">
                <span className="circle-text">{step.title}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. "How DuWise Drives Business Impact" Section */}
      <section className="business-impact-section">
        <div className="impact-header">
          <h2 className="impact-title">
            How DuWise Drives <span className="highlight-blue">Business Impact</span>
          </h2>
          <p className="impact-desc">
            The product features translate into opportunities across acquisition, engagement, retention, differentiation, and operational quality.
          </p>
        </div>

        <div className="impact-cards-row">
          {impactCards.map((item, idx) => (
            <div className="impact-card" key={idx}>
              <div className="impact-num">{item.num}</div>
              <h3 className="impact-card-title">{item.title}</h3>
              <p className="impact-card-body">{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5. "Real Problems. Smarter Solutions" Section */}
      <section className="real-problems-section">
        <div className="problems-header">
          <h2 className="problems-title">
            Real Problems. <span className="highlight-blue">Smarter Solutions</span>
          </h2>
          <p className="problems-desc">
            DuWise is more than a document-storage application. Its proposition is built around capturing important records, structuring their information, tracking dates, reminding users at the right time, and protecting access to those records across connectivity and device changes.
          </p>
        </div>

        <div className="problems-grid">
          {/* Top row: 3 cards */}
          <div className="problems-row top-row">
            {problemCards.slice(0, 3).map((item, idx) => (
              <div className="problem-card" key={idx}>
                <div className="card-header-line">
                  <div className="card-icon-box">{item.icon}</div>
                  <span className="card-header-title">{item.title}</span>
                </div>

                <div className="card-dual-boxes">
                  <div className="box-problem">
                    <span className="badge-label">The Problem</span>
                    <p className="badge-text">{item.problem}</p>
                  </div>
                  <div className="box-solution">
                    <span className="badge-label">DuWise Response</span>
                    <p className="badge-text">{item.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom row: 2 cards centered */}
          <div className="problems-row bottom-row">
            {problemCards.slice(3).map((item, idx) => (
              <div className="problem-card" key={idx + 3}>
                <div className="card-header-line">
                  <div className="card-icon-box">{item.icon}</div>
                  <span className="card-header-title">{item.title}</span>
                </div>

                <div className="card-dual-boxes">
                  <div className="box-problem">
                    <span className="badge-label">The Problem</span>
                    <p className="badge-text">{item.problem}</p>
                  </div>
                  <div className="box-solution">
                    <span className="badge-label">DuWise Response</span>
                    <p className="badge-text">{item.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Pre-Footer Contact Section */}
      <HomePageFooter page="DuWise" />

      {/* 7. Website Footer */}
      <Footer />
    </div>
  );
};

export default DuWise;
